import {
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  FormControlLabel,
  Menu,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Fade,
  IconButton,
  Tooltip,
  Switch,
  TableSortLabel,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import api from "../axiosInstance";
import { Link } from "react-router-dom";
import NewsModal from "./NewsModal";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { toast, ToastContainer } from "react-toastify";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";

// Column config and visibility state
const allColumns = [
  {
    key: "id",
    label: "ID",
    cellCss: { align: "center", minWidth: "50px" },
    getValue: (r) => r.id,
  },
  {
    key: "universityId",
    label: "University Id",
    cellCss: { align: "center", minWidth: "100px" },
    getValue: (r) => r.universityId,
  },
  {
    key: "title",
    label: "Title",
    cellCss: { align: "center", minWidth: "203px" },
    getValue: (r) => r.title,
  },
  {
    key: "category",
    label: "Category",
    cellCss: { align: "center", minWidth: "100px" },
    getValue: (r) => r.category,
  },
  {
    key: "universityName",
    label: "University Name",
    cellCss: { align: "center", minWidth: "140px" },
    getValue: (r) => r.universityName,
  },
  {
    key: "additionalText",
    label: "Additional Text",
    cellCss: { align: "center", minWidth: "120px" },
    getValue: (r) => r.additionalText,
  },
  {
    key: "description",
    label: "Description",
    cellCss: { align: "center", minWidth: "203px" },
    getValue: (r) => r.description,
  },
  {
    key: "contentType",
    label: "Content Type",
    cellCss: { align: "center", minWidth: "120px" },
    getValue: (r) => r.contentType || "-",
  },
  {
    key: "contentUrl",
    label: "Content Url",
    cellCss: {
      align: "center",
      minWidth: "200px",
      overflowWrap: "break-word",
      wordBreak: "break-word",
      whiteSpace: "normal",
    },
    getValue: (r) => r.contentUrl ?? "-",
  },
  {
    key: "linkUrl",
    label: "Link Url",
    cellCss: {
      align: "center",
      minWidth: "200px",
      overflowWrap: "break-word",
      wordBreak: "break-word",
      whiteSpace: "normal",
    },
    getValue: (r) => r.linkUrl,
  },
  {
    key: "displayDate",
    label: "Display Date",
    cellCss: { align: "center", minWidth: "100px" },
    getValue: (r) => r.displayDate,
  },

  {
    key: "displayOrder",
    label: "Display Order",
    cellCss: { align: "center", minWidth: "120px" },
    getValue: (r) => r.displayOrder,
  },
  {
    key: "displayTime",
    label: "Display Time",
    cellCss: { align: "center", minWidth: "100px" },
    getValue: (r) => r.displayTime,
  },
  {
    key: "active",
    label: "Active",
    getValue: (r) => (r.active ? "Yes" : "No"),
  },
  {
    key: "createdBy",
    label: "Created By",
    cellCss: { align: "center", minWidth: "100px" },
    getValue: (r) => r.createdBy,
  },
  {
    key: "createdDate",
    label: "Created Date",
    cellCss: { align: "center", minWidth: "100px" },
    getValue: (r) => r.createdDate,
  },

  {
    key: "lastUpdatedBy",
    label: "Last Updated By",
    cellCss: {
      align: "center",
      minWidth: "140px",
    },
    getValue: (r) => r.lastUpdatedBy,
  },
  {
    key: "lastUpdatedDate",
    label: "Last Updated Date",
    getValue: (r) => r.lastUpdatedDate,
  },
];
const defaultVisibleKeys = new Set([
  "title",
  "category",
  "contentType",
  "contentUrl",
  "description",
  "displayOrder",
  "linkUrl",
  "universityName",
]);

const STORAGE_KEY = "newsVisibleColumns";

function Updates() {
  const [openNew, setOpenNew] = useState(false);
  const handleNewClose = () => setOpenNew(false);
  const [newsList, setNewsList] = useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(30);
  const [newsEdit, setNewsEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState(() => {
    return localStorage.getItem("news_updates") || "";
  });
  const [dense, setDense] = useState(true);
  const [orderBy, setOrderBy] = useState("title");
  const [order, setOrder] = useState("asc");

  // Save to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("news_updates", searchQuery);
  }, [searchQuery]);
  // Sorting logic
  const handleSort = (columnKey) => {
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
  };
  //  Filter data
  const filteredCount = newsList.filter(
    (news) =>
      news.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      news.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      news.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sorted Data news Updates
  const sortedData = useMemo(() => {
    if (!filteredCount) return [];

    return [...filteredCount].sort((a, b) => {
      if (!orderBy) return 0;

      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === "string") {
        return order === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  }, [filteredCount, orderBy, order]);

  // padding Dense
  const handleChangeDense = (event) => {
    setDense(event.target.checked);
  };
  // Deletion confirm dialog box open and close.
  const handleClickOpen = (id) => {
    setSelectedId(id);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedId(null);
  };

  // Initialize from localStorage synchronously to avoid a race with the persister effect
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          const validKeys = new Set(allColumns.map((c) => c.key));
          const filtered = arr.filter((k) => validKeys.has(k));
          if (filtered.length > 0) {
            return new Set(filtered);
          }
        }
      }
    } catch {}
    return new Set(Array.from(defaultVisibleKeys));
  });

  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const openColumnsMenu = Boolean(columnsMenuAnchor);

  // Persist column config to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(Array.from(visibleColumns))
      );
    } catch {}
  }, [visibleColumns]);

  const handleOpenColumnsMenu = (e) => setColumnsMenuAnchor(e.currentTarget);
  const handleCloseColumnsMenu = () => setColumnsMenuAnchor(null);
  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Helper function to get boolean chip styling
  const getBooleanChip = (value) => {
    return (
      <Chip
        label={value ? "Yes" : "No"}
        color={value ? "success" : "error"}
        size="small"
        variant="outlined"
      />
    );
  };

  // Helper function to get content type chip styling
  const getContentTypeChip = (type) => {
    const typeConfig = {
      text: { color: "primary", label: "Text" },
      image: { color: "secondary", label: "Image" },
      video: { color: "success", label: "Video" },
      link: { color: "info", label: "Link" },
    };

    const config = typeConfig[type?.toLowerCase()] || {
      color: "default",
      label: type || "Unknown",
    };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  //  Update news get Api Fetching
  useEffect(() => {
    setIsLoading(true);
    setError(null);
    api
      .get("/api/v1/update-news")
      .then((res) => {
        setNewsList(res.data.data.content);
        setError(null);
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to load news updates. Please try again.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  // After successful add/update
  const handleFormSuccess = (newItem) => {
    if (!newItem || !newItem.id) {
      console.warn("Invalid item received from form", newItem);
      return;
    }
    // update locally
    setNewsList((prev) => {
      const exists = prev.some((item) => item.id === newItem.id);
      // If editing: replace the item
      if (exists) {
        return prev.map((item) => (item.id === newItem.id ? newItem : item));
      } else {
        // If adding: push new item
        return [newItem, ...prev];
      }
    });

    setNewsEdit(null);
  };
  //   Page nation handel changes
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // page nation rows handles
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };
  // Delete The record
  const handleDelete = async () => {
    try {
      await api.delete(`/api/v1/update-news/${selectedId}`);

      // If deletion is successful, update local state update
      setNewsList((prev) => prev.filter((row) => row.id !== selectedId));
      toast.success("Record deleted successfully.!", {
        position: "top-right",
        autoClose: 3000,
      });
      setOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete the record. Please try again.");
    }
  };
  // Adding page data in onclick
  const handleNewOpen = () => {
    setOpenNew(true);
    setNewsEdit([]);
  };
  // Editing page data in onclick
  const handleEditClick = (rowData) => {
    handleNewOpen();
    setNewsEdit(rowData);
  };

  // pagenation code
  const paginatedNewsUpdate = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  console.log(paginatedNewsUpdate);

  // Columns Field showing obeject
  const visibleColumnDefs = allColumns.filter((c) => visibleColumns.has(c.key));
  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <ToastContainer />

      {/* Header Section */}
      {/* <Card sx={{ mb: 3, boxShadow: 2 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box>
              <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                <Link underline="hover" color="inherit" to="/booklist">
                  Books List
                </Link>
                <Typography sx={{ color: "text.primary", fontWeight: 600 }}>
                  News & Updates Management
                </Typography>
              </Breadcrumbs>
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontWeight: 600, color: "primary.main" }}
              >
                News & Updates
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()}
              disabled={isLoading}
              sx={{ minWidth: 120 }}
            >
              {isLoading ? <CircularProgress size={20} /> : "Refresh"}
            </Button>
          </Box>
        </CardContent>
      </Card> */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" }, // column on mobile, row on desktop
          justifyContent: { xs: "flex-start", md: "space-between" }, // start on mobile, space-between on desktop
          alignItems: { xs: "stretch", md: "center" }, // stretch on mobile, center on desktop
          gap: { xs: 1, md: 0 }, // spacing between items
          mb: 2,
          marginLeft: "10px",
        }}
      >
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
          <Link underline="hover" color="inherit" to="/booklist">
            Books List
          </Link>
          <Typography sx={{ color: "text.primary", fontWeight: 600 }}>
            News & Updates Management
          </Typography>
        </Breadcrumbs>

        <Box>
          {/* Action Buttons */}
          <Stack
            direction="row"
            spacing={2}
            flexWrap="wrap"
            useFlexGap
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: { xs: "wrap", md: "nowrap" }, // wrap buttons on mobile
              mt: { xs: 1, md: 0 },
              marginRight: "10px",
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ViewColumnIcon />}
              onClick={handleOpenColumnsMenu}
              sx={{
                minWidth: { xs: "120px", md: "auto" }, // smaller on mobile
                fontSize: { xs: "10px", md: "12px" },
              }}
            >
              Columns
            </Button>
            <Menu
              sx={{ maxWidth: "270px", maxHeight: "500px" }}
              anchorEl={columnsMenuAnchor}
              open={openColumnsMenu}
              onClose={handleCloseColumnsMenu}
            >
              {allColumns.map((col) => (
                <FormControlLabel
                  key={col.key}
                  control={
                    <Checkbox
                      checked={visibleColumns.has(col.key)}
                      onChange={() => toggleColumn(col.key)}
                    />
                  }
                  label={col.label}
                  sx={{ pl: 1, pr: 2 }}
                />
              ))}
            </Menu>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleNewOpen}
              sx={{
                minWidth: { xs: "120px", md: "auto" }, // smaller on mobile
                fontSize: { xs: "10px", md: "12px" },
              }}
            >
              Add News
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Search Section */}
      <Card sx={{ boxShadow: 1 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                minWidth: { xs: "80%", md: "50%" },
                display: "flex",
                width: "50%",
                alignItems: "center",
                gap: 2,
              }}
            >
              <TextField
                size="small"
                fullWidth
                variant="outlined"
                placeholder="Search news by title, category, description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ maxWidth: 400 }}
              />
              <Typography
                sx={{ display: { xs: "none", md: "block" } }}
                variant="body2"
                color="text.secondary"
              >
                {
                  newsList.filter(
                    (news) =>
                      news.title
                        ?.toLowerCase()
                        .includes(searchQuery.toLowerCase()) ||
                      news.category
                        ?.toLowerCase()
                        .includes(searchQuery.toLowerCase()) ||
                      news.description
                        ?.toLowerCase()
                        .includes(searchQuery.toLowerCase())
                  ).length
                }{" "}
                of {newsList.length} news items
              </Typography>
            </Box>
            <Box>
              {/* Dense toggle */}
              <Tooltip title="Dense padding" arrow>
                <FormControlLabel
                  control={
                    <Switch
                      checked={dense}
                      onChange={handleChangeDense}
                      color="primary"
                    />
                  }
                />
              </Tooltip>
            </Box>
          </Box>
          <Typography
            sx={{ display: { xs: "block", md: "none" }, textAlign: "left" }}
            variant="body2"
            color="text.secondary"
          >
            {
              newsList.filter(
                (news) =>
                  news.title
                    ?.toLowerCase()
                    .includes(searchQuery.toLowerCase()) ||
                  news.category
                    ?.toLowerCase()
                    .includes(searchQuery.toLowerCase()) ||
                  news.description
                    ?.toLowerCase()
                    .includes(searchQuery.toLowerCase())
              ).length
            }{" "}
            of {newsList.length} news items
          </Typography>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {/* Table Section */}
      {!isLoading && (
        <Fade in={!isLoading}>
          <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: 2 }}>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table
                stickyHeader
                aria-label="news table"
                size={dense ? "small" : "medium"}
              >
                <TableHead>
                  <TableRow>
                    {visibleColumnDefs.map((c) => (
                      <TableCell
                        key={c.key}
                        sx={{
                          padding: "10px 5px ",
                          backgroundColor: "#17479e",
                          color: "white",
                          fontWeight: 600,
                          ...c.cellCss,
                          position: "sticky",
                          top: 0,
                          zIndex: 3,
                        }}
                        sortDirection={orderBy === c.key ? order : false}
                      >
                        <TableSortLabel
                          active={orderBy === c.key}
                          direction={orderBy === c.key ? order : "asc"}
                          onClick={() => handleSort(c.key)}
                          sx={{
                            color: "#ffffff !important",
                            "&.Mui-active": {
                              color: "#ffffff !important",
                            },
                            "& .MuiTableSortLabel-icon": {
                              color: "#ffffff !important",
                            },
                          }}
                        >
                          {c.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell
                      sx={{
                        backgroundColor: "#17479e",
                        color: "white",
                        fontWeight: 600,
                        minWidth: 120,
                        position: "sticky",
                        top: 0,
                        right: 0,
                        zIndex: 4,
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(paginatedNewsUpdate) &&
                  paginatedNewsUpdate.length > 0 ? (
                    paginatedNewsUpdate
                      .filter(
                        (news) =>
                          !searchQuery ||
                          news.title
                            ?.toLowerCase()
                            .includes(searchQuery.toLowerCase()) ||
                          news.category
                            ?.toLowerCase()
                            .includes(searchQuery.toLowerCase()) ||
                          news.description
                            ?.toLowerCase()
                            .includes(searchQuery.toLowerCase())
                      )
                      .map((row) => (
                        <TableRow
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          key={row.id}
                          sx={{
                            "&:nth-of-type(odd)": {
                              backgroundColor: "rgba(0, 0, 0, 0.02)",
                            },
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                        >
                          {visibleColumnDefs.map((c) => (
                            <TableCell
                              key={c.key}
                              sx={{ padding: "5px 10px", ...c.cellCss }}
                            >
                              {c.key === "active"
                                ? getBooleanChip(c.getValue(row))
                                : c.key === "contentType"
                                ? getContentTypeChip(c.getValue(row))
                                : c.getValue(row)}
                            </TableCell>
                          ))}
                          <TableCell
                            sx={{
                              align: "center",
                              padding: "10px 10px",
                              minWidth: "120px",
                              position: "sticky",
                              right: 0,
                              backgroundColor: "#fff",
                              zIndex: 1,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={0.5}
                              justifyContent="center"
                            >
                              <Tooltip title="Edit News" arrow placement="top">
                                <IconButton
                                  color="primary"
                                  onClick={() => handleEditClick(row)}
                                  size="small"
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: "primary.light",
                                      color: "white",
                                    },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip
                                title="Delete News"
                                arrow
                                placement="top"
                              >
                                <IconButton
                                  color="error"
                                  onClick={() => handleClickOpen(row.id)}
                                  size="small"
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: "error.light",
                                      color: "white",
                                    },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={visibleColumnDefs.length + 1}
                        sx={{ textAlign: "center", py: 4 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Typography variant="h6" color="text.secondary">
                            {searchQuery
                              ? "No news found matching your search"
                              : "No news available"}
                          </Typography>
                          {searchQuery && (
                            <Button
                              variant="outlined"
                              onClick={() => setSearchQuery("")}
                              startIcon={<SearchIcon />}
                            >
                              Clear Search
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 30, 50, 100]}
              component="div"
              count={filteredCount.length || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
                "& .MuiTablePagination-toolbar": {
                  paddingLeft: 2,
                  paddingRight: 2,
                },
              }}
            />
          </Paper>
        </Fade>
      )}
      {/* Modals */}
      <NewsModal
        newsEdit={newsEdit}
        open={openNew}
        onClose={handleNewClose}
        onSuccess={handleFormSuccess}
      />

      {/* Confirm Delete Dialog */}
      <DeleteConfirmationDialog
        open={open}
        onClose={handleClose}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this news item? This action cannot be undone."
        confirmText="DELETE"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </Box>
  );
}

export default Updates;
